import { QURAN_CREDIT, kemenagUrl } from '@iqrapath/shared'
import { ChevronRight } from 'lucide-react'
import { Link } from 'react-router-dom'
import { AudioButton } from '../../components/AudioButton.tsx'
import { Obj } from '../../components/Obj.tsx'
import { SourceLink } from '../../components/SourceLink.tsx'
import { setLatinOn, useLatinOn } from '../../lib/prefs.ts'
import { arNum, type AyatDetail, type Verse } from './ayat.ts'
import { CtaBar, NextStep } from './parts.tsx'

/** Kemenag marks footnotes inline as "N)": shown as a superscript, explained under the verse. */
export function Terjemah({ text }: { text: string }) {
  return <>{text.split(/(\d+\))/).map((p, i) => (i % 2 ? <sup key={i} className="fn-mark">{p.slice(0, -1)}</sup> : p))}</>
}

function VerseCard({ v, surah, latin }: { v: Verse; surah: number; latin: boolean }) {
  return (
    <article className="card verse" aria-label={`Ayat ${v.k}`}>
      <div className="verse-head">
        <span className="pill pill-mint">Ayat {v.k}</span>
        {v.audio && <AudioButton id={`v-${surah}-${v.k}`} srcs={[v.audio]} what={`ayat ${v.k}`} />}
      </div>
      <p className="ar verse-ar" lang="ar" dir="rtl">{v.ar} <span className="end">۝{arNum(v.k)}</span></p>
      {latin && v.latin && <p className="lat">{v.latin}</p>}
      <p className="tr"><Terjemah text={v.terjemah} /></p>
      {v.footnotes && <p className="fn">{v.footnotes}</p>}
    </article>
  )
}

/** Step 1: read (and listen). */
export function Iqra({ d, day }: { d: AyatDetail; day: number }) {
  const a = d.ayat
  const latin = useLatinOn()
  return (
    <>
      <div className="step-top">
        <p className="step-hint">Baca perlahan. Boleh dengarkan dulu.</p>
        {a.verses?.some((v) => v.latin) && (
          <div className="mini-switch">
            <span id="lat-l">Latin</span>
            <button type="button" role="switch" aria-checked={latin} aria-labelledby="lat-l" className="switch" onClick={() => setLatinOn(!latin)} />
          </div>
        )}
      </div>

      {a.verses ? (
        <>
          <div className="stack">{a.verses.map((v) => <VerseCard key={v.k} v={v} surah={a.surah} latin={latin} />)}</div>
          <p className="credit"><SourceLink href={kemenagUrl(a.surah, a.ayatFrom, a.ayatTo)}>{QURAN_CREDIT}</SourceLink></p>
        </>
      ) : (
        <div className="card note-card">
          <b>{a.ref}</b>
          <p className="muted">Teks ayat akan tampil di sini setelah data Al-Qur’an terpasang.</p>
        </div>
      )}

      <div className="card acc">
        <details>
          <summary>Kenapa cuma satu ayat sehari?</summary>
          <p className="prose">
            Satu ayat yang dipahami lalu dijalani biasanya lebih membekas daripada banyak ayat yang lewat begitu saja. Sedikit
            tapi tiap hari memberi waktu bagi maknanya untuk meresap, dan besok ada ayat baru yang menunggu.
          </p>
        </details>
      </div>

      <Link className="card row-card" to={`/quran/${a.surah}?ayat=${a.ayatFrom}`}>
        <span className="ic obj-ic" aria-hidden="true"><Obj id="mushaf" /></span>
        <div><b>Buka surat lengkap</b><small>Baca ayat ini bersama ayat-ayat di sekitarnya.</small></div>
        <ChevronRight className="chev" size={18} aria-hidden="true" />
      </Link>

      <CtaBar><NextStep day={day} step="fahami" /></CtaBar>
    </>
  )
}
